import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import Globalize from "globalize";
import ColorSwatch from "components/color-swatch";
import IconButton from "material-ui/IconButton";
import CloseIcon from "material-ui/svg-icons/navigation/close";
import theme from "styles/theme";
import { themeOptions } from "styles/theme";
import { style, merge, $ } from "glamor";
import classNames from "classnames";
import ColorChips from "./color-chips";
import { displayingNewColorways } from "helpers/swatch-utils";
import Slider from "react-slick";

import "styles/slick/slick-theme.styl";
import "styles/slick/slick.styl";

import { PrevArrow, NextArrow } from "components/product/summary";
import { ITEM_WIDTH, HORIZONTAL_OFFSET } from "components/items/views/panel";
import { DATATEST } from "constants/integration";

const CHIP_STYLE = { width: "32px" };

const COLOR_CHIP_COUNT = 16;

const ColorSwatchGrid = ({ variants, onSwatchSelected, selectedVariant, showTooltips, isGrapesJs }) => {
    return (
        <React.Fragment>
            {variants.map((variant, i) => (
                <ColorSwatch
                    key={`${variant._id}-${i}`}
                    onClick={onSwatchSelected}
                    selected={variant._id === selectedVariant._id}
                    item={variant}
                    swatch={variant.color_swatch}
                    tooltip={showTooltips && variant.display_name}
                    style={CHIP_STYLE}
                    isGrapesJs={isGrapesJs}
                />
            ))}
        </React.Fragment>
    );
};

const ColorSwatchCarousel = ({
    variants,
    onSwatchSelected,
    selectedVariant,
    chipsCountOnSlide = COLOR_CHIP_COUNT,
    isGrapesJs,
}) => {
    const slides = React.useMemo(() => {
        const _slides = [];
        let slide = [];

        variants.forEach((color_swatch, i) => {
            if (i % chipsCountOnSlide === 0 && slide.length) {
                _slides.push(<div key={i}>{slide}</div>);
                slide = [];
            }
            slide.push(
                <ColorSwatch
                    item={{
                        _id: color_swatch.ref,
                        ref: color_swatch.ref,
                        name: color_swatch.swatch_label || color_swatch.display_name,
                    }}
                    onClick={onSwatchSelected}
                    key={i}
                    swatch={color_swatch.color_swatch}
                    selected={color_swatch.ref === selectedVariant._id}
                    tooltip={color_swatch.swatch_label || color_swatch.display_name}
                    style={CHIP_STYLE}
                    isGrapesJs={isGrapesJs}
                />
            );
        });

        if (slide.length) {
            _slides.push(<div key={variants.length}>{slide}</div>);
        }

        return _slides;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [variants]);

    const settings = {
        dots: false,
        lazyLoad: true,
        infinite: false,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        responsive: [
            {
                breakpoint: 600,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                },
            },
        ],
    };

    return (
        <div className="sliderContainer">
            <Slider {...settings}>{slides}</Slider>
        </div>
    );
};

class ColorPreview extends PureComponent {
    static propTypes = {
        item: PropTypes.object.isRequired,
        variants: PropTypes.array.isRequired,
        onSwatchSelected: PropTypes.func,
        showTooltips: PropTypes.bool,
        small: PropTypes.bool.isRequired,
        onToggleViewMore: PropTypes.func,
        activeBrand: PropTypes.object,
        maxChipsAmount: PropTypes.number,
    };

    static defaultProps = {
        small: false,
        showTooltips: true,
    };

    styles = {
        container: merge(
            {
                position: "absolute",
                height: "20px",
                zIndex: 12,
                backgroundColor: "rgba(255,255,255,0)",
                transition: "all 0.2s ease-out",
            },
            $(".showAll", {
                backgroundColor: "rgba(255,255,255,.95)",
                top: "0px",
                height: "100%",
            }),
            $(" .swatchContainer", {
                overflowX: this.props.isGrapesJs ? "visible" : "hidden",
                overflowY: this.props.isGrapesJs ? "visible" : "hidden",
                position: "absolute",
                top: "0px",
                left: "0px",
                right: "0px",
                bottom: "0px",
            }),
            $(" .swatchContainer.selected", {
                width: "32px !important",
            }),
            $(" .swatchContainerShowAll", {
                overflowY: this.props.isGrapesJs ? "visible" : "auto",
                top: "24px",
            }),
            $(".small .swatchContainer > a", {
                fontSize: "12px",
            }),
            $(" .sliderContainer", {
                padding: "8px 16px 0 16px",
                position: "relative",
                width: `${ITEM_WIDTH - HORIZONTAL_OFFSET * 2}px`,
            })
        ),
        more: style({
            fontSize: "14px",
            display: "inline-block",
            height: "14px",
            lineHeight: "14px",
            verticalAlign: "top",
        }),
        less: {
            width: "32px",
            height: "32px",
            position: "absolute",
            top: "-8px",
            right: "0px",
        },
        lessIcon: {
            width: "16px",
            height: "16px",
        },
    };

    constructor(props) {
        super(props);
        this.state = {
            showAll: false,
            selectedVariant: props.item,
        };

        this.handleViewMore = this.handleViewMore.bind(this);
        this.handleCloseViewMore = this.handleCloseViewMore.bind(this);
        this.handleSwatchSelected = this.handleSwatchSelected.bind(this);
    }

    handleViewMore(e) {
        e.preventDefault();
        e.stopPropagation();
        this.props.onToggleViewMore?.(true);
        this.setState({ showAll: true });
    }

    handleCloseViewMore(e) {
        e.preventDefault();
        e.stopPropagation();
        this.props.onToggleViewMore?.(false);
        this.setState({ showAll: false });
    }

    handleSwatchSelected(e, variant) {
        this.props.onSwatchSelected(e, { _id: variant.ref, name: variant.display_name });
        this.setState({ selectedVariant: variant });
    }

    render() {
        const {
            item,
            variants,
            small,
            showTooltips,
            activeBrand,
            maxChipsAmount,
            chipsCountOnSlide,
            isGrapesJs,
            onSwatchSelected,
        } = this.props;
        const { showAll, selectedVariant } = this.state;

        const colorChipsEnabled = displayingNewColorways(activeBrand);
        const showColorChips = !showAll && variants.length && colorChipsEnabled && !small;
        const showText = !showAll && variants.length && (!colorChipsEnabled || small);

        return (
            <div
                {...this.styles.container}
                className={classNames("swatches", {
                    showAll: (!colorChipsEnabled || small) && showAll,
                    small,
                    colorChipsEnabledCollapsed: colorChipsEnabled && !small && !showAll,
                    colorChipsEnabledOpen: colorChipsEnabled && !small && showAll,
                })}
                data-test={DATATEST.GALLERY.SWATCH_CONTAINER}
            >
                {showAll && (
                    <IconButton
                        data-test={DATATEST.GALLERY.CLOSE_SWATCH_CONTAINER}
                        style={this.styles.less}
                        tooltip={Globalize.formatMessage("Close")}
                        onClick={this.handleCloseViewMore}
                    >
                        <CloseIcon style={this.styles.lessIcon} color={themeOptions.palette.darkerGrey} />
                    </IconButton>
                )}
                <div className={classNames("swatchContainer", { swatchContainerShowAll: showAll })}>
                    {showAll && (!colorChipsEnabled || small) && (
                        <ColorSwatchGrid
                            showTooltips={showTooltips}
                            onSwatchSelected={this.handleSwatchSelected}
                            variants={variants}
                            selectedVariant={selectedVariant}
                            isGrapesJs={isGrapesJs}
                        />
                    )}
                    {showAll && colorChipsEnabled && !small && (
                        <ColorSwatchCarousel
                            selectedVariant={selectedVariant}
                            onSwatchSelected={this.handleSwatchSelected}
                            variants={variants}
                            chipsCountOnSlide={chipsCountOnSlide}
                            isGrapesJs={isGrapesJs}
                        />
                    )}
                    {showColorChips && (
                        <ColorChips
                            item={item}
                            variants={variants}
                            brand={activeBrand}
                            maxChipsAmount={maxChipsAmount}
                            onViewMore={this.handleViewMore}
                            onSwatchSelected={onSwatchSelected}
                            isGrapesJs={isGrapesJs}
                        />
                    )}
                    {showText && (
                        <a key="more" href="#" onClick={this.handleViewMore} {...this.styles.more}>
                            {Globalize.formatMessage("more colors")}
                        </a>
                    )}
                </div>
            </div>
        );
    }
}

export default ColorPreview;
